import { useCallback } from 'react';
import { SUBSCRIPTIONS_CHECKOUT_PATH } from '~/lib/constants';
import CheckoutFrame, { CheckoutHostMessage } from '~/components/checkout-embed/checkout-frame';
import { SubscriptionInterval, SubscriptionPlanTarget, SubscriptionTier } from './types';

export type SubscriptionCheckoutTarget = SubscriptionPlanTarget;

export interface SubscriptionCompleteResult {
  subscriptionId: string;
  tier: SubscriptionTier;
  interval: SubscriptionInterval;
}

interface props {
  target: SubscriptionCheckoutTarget;
  title: string;
  /** Change this to re-load the SAME target - e.g. after recovering a lapsed session. */
  reloadNonce?: number;
  onComplete: (result: SubscriptionCompleteResult) => void;
  onSkip: () => void;
  onChangePlan: () => void;
  /**
   * Drop the frame's own "Skip - continue on Free plan" and "Choose a different plan" links,
   * because this host renders its own way out of the step.
   *
   * `onSkip`/`onChangePlan` stay required either way: the messages are simply never posted, and a
   * caller that sets this must still handle them if it ever unsets it. Setting this means taking on
   * BOTH jobs - somewhere to go without subscribing, and somewhere to choose another plan.
   */
  hideSecondaryActions?: boolean;
  /** The frame has no live session. The host must resolve this - see the case below. */
  onAuthRequired: (lookupKey: string | null) => void;
}

/** The frame is loaded for exactly ONE plan - it has no picker of its own, the host does. */
function targetParams(target: SubscriptionCheckoutTarget): Record<string, string> {
  return 'plan' in target ? { plan: target.plan } : { tier: target.tier, interval: target.interval };
}

/**
 * Embeds ui-checkout's single-plan subscription checkout (its `docs/EMBEDDED_PAYMENT.md` →
 * "Embedding the subscription checkout").
 *
 * The bridge itself - URL flags, the three-check listener, resizing - belongs to `CheckoutFrame`.
 * All that lives here is the five `subscription:*` messages that are specific to this route. The
 * exception is `subscription:step`: `CheckoutFrame` handles it itself (it scrolls the frame into view)
 * and never forwards it, so it will not reach this component's `onMessage` handler.
 */
const SubscriptionCheckoutFrame = ({
  target,
  title,
  reloadNonce = 0,
  onComplete,
  onSkip,
  onChangePlan,
  onAuthRequired,
  hideSecondaryActions = false
}: props) => {
  const handleMessage = useCallback(
    (message: CheckoutHostMessage) => {
      switch (message.type) {
        case 'subscription:complete':
          // Ids only, never a URL - the host owns its own routing.
          onComplete({
            subscriptionId: message.subscriptionId as string,
            tier: message.tier as SubscriptionTier,
            interval: message.interval as SubscriptionInterval
          });
          break;
        case 'subscription:skip':
          onSkip();
          break;
        case 'subscription:change-plan':
          onChangePlan();
          break;
        case 'subscription:auth-required':
          // The contract marks this must-act. The frame renders a Sign-in button of its own, but
          // pressing it starts an OIDC redirect INSIDE the iframe and most identity providers
          // refuse to be framed, so the customer gets a blank frame rather than a login form. The
          // host has to resolve it in the top window.
          onAuthRequired(typeof message.lookupKey === 'string' ? message.lookupKey : null);
          break;
        default:
          // `subscription:payment` mirrors checkout's own funnel events - optional, and nothing
          // here consumes it.
          break;
      }
    },
    [onComplete, onSkip, onChangePlan, onAuthRequired]
  );

  return (
    <CheckoutFrame
      path={SUBSCRIPTIONS_CHECKOUT_PATH}
      params={{
        ...targetParams(target),
        ...(hideSecondaryActions && { hideSecondaryActions: 'true' })
      }}
      title={title}
      reloadNonce={reloadNonce}
      onMessage={handleMessage}
      minHeight={null}
    />
  );
};

export default SubscriptionCheckoutFrame;
